import React, { useContext, useEffect, useState, useRef, use } from "react"
import moment from "moment"
import ServiceCardBannerOverlay from "../services/ServiceCardBannerOverlay"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { CoachPublicProfileContext } from "../../pages/manage-business/profile/context/coachPublicProfileContext"
import { ContactRequests } from "../../api/app.service"
import { getLightTextColor } from "../../utils/getLightTextColor"
import { getOrdinal } from "../../utils/get-ordinal"
import { getPrice } from "../../utils/services/get-price"
import { getTextColor } from "../../utils/getTextColor"
import { getUnits } from "../../utils/services/get-units/get-units"
import { isCoachInCommissionTracking } from "../../utils/commissionTracking"
import { pluralize } from "../../utils/pluralize"
import { ServiceRequests } from "../../api/app.service"
import { SvgClock } from "../icons"
import { useAuthenticatedUser } from "../../hooks/useAuthenticatedUser"
import { useParams, useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  service: any
  getServices: any
}

const PublicProfileServiceCard: React.FC<Props> = ({ service }) => {
  const { user } = useContext(UserCtx)
  const { is_group_service } = service
  const { toast } = useToast()
  const { endpoint_slug } = useParams()
  const { coachPublicProfileData } = useContext(CoachPublicProfileContext)
  const [userContacts, setUserContacts] = useState<any>([])
  const [spotsLeft, setSpotsLeft] = useState<number | null>(null)
  const [showRecurrencePopover, setShowRecurrencePopover] = useState(false)
  const navigate = useNavigate()
  const serviceDetails = service?.service_details
  const coachColor = coachPublicProfileData?.profile_information?.profileColor
  const textColor = getLightTextColor(coachColor)
  const subscribedCoach = coachPublicProfileData.subscription_id !== null
  const authenticatedUser = useAuthenticatedUser(user)
  const notSameUser =
    Number(coachPublicProfileData?.user_id) !== Number(user?.user_id) &&
    subscribedCoach
  const groupService = service?.is_group_service === true

  const unauthenticatedFreeConsult =
    !authenticatedUser &&
    service?.service_details?.type?.toLowerCase() === "consultation" &&
    Number(service.service_details?.billing?.amount) === 0

  const unauthenticatedPaidService =
    !authenticatedUser &&
    Number(service.service_details?.billing?.amount) !== 0 &&
    subscribedCoach

  const unauthenticatedFreeService =
    !authenticatedUser &&
    service?.service_details?.type?.toLowerCase() !== "consultation" &&
    Number(service.service_details?.billing?.amount) === 0

  const recurrencePopoverRef = useRef<HTMLDivElement>(null)
  const recurrenceTriggerRef = useRef<HTMLButtonElement>(null)

  const getServiceCardDetailsLink = (e: any) => {
    if (unauthenticatedFreeConsult) {
      navigate(
        `/providers/${coachPublicProfileData?.endpoint_slug}/booking/${service?.service_id}`
      )
    } else if (unauthenticatedFreeService) {
      navigate(
        `/providers/${coachPublicProfileData?.endpoint_slug}/services/${service?.service_id}`
      )
    } else if (unauthenticatedPaidService) {
      navigate(
        `/providers/${coachPublicProfileData?.endpoint_slug}/checkout/${service?.service_id}`
      )
    } else {
      checkEmailOrRedirect()
    }
  }

  const getContacts = () => {
    if (user?.coachProfile) {
      ContactRequests.getContactList({
        profile_id: user.activeProfileId,
        profile_type: user.activeProfile,
      })
        .then((data) => {
          setUserContacts(data.contact_list)
        })
        .catch((ex) => {
          toast.error(ex.response.data.message)
        })
    }
  }

  useEffect(() => {
    getContacts()
  }, [])

  const isConnected = userContacts.some(
    (contact: any) => contact?.user_id === coachPublicProfileData?.user_id
  )

  const checkEmailOrRedirect = () => {
    if (authenticatedUser && notSameUser && !isConnected) {
      ContactRequests.createContactConnection({
        sender_profile_id: coachPublicProfileData?.coach_profile_id,
        receiver_profile_id: user.activeProfileId,
        user_environment: process.env.REACT_APP_USER_ENVIRONMENT || "",
        status: user?.isCoach
          ? "c2c"
          : isCoachInCommissionTracking(coachPublicProfileData?.endpoint_slug)
          ? "zoee lead"
          : "connected",
      }).then(() => {
        user?.isCoach
          ? navigate(
              `/coach/${coachPublicProfileData?.endpoint_slug}/services/${service?.service_id}`
            )
          : navigate(
              `/member/${coachPublicProfileData?.endpoint_slug}/services/${service?.service_id}`
            )
      })
    } else if (authenticatedUser && notSameUser && isConnected) {
      user?.isCoach
        ? navigate(
            `/coach/${coachPublicProfileData?.endpoint_slug}/services/${service?.service_id}`
          )
        : navigate(
            `/member/${coachPublicProfileData?.endpoint_slug}/services/${service?.service_id}`
          )
    } else {
      navigate(`/providers/${endpoint_slug}/services/${service.service_id}`)
    }
  }

  const getSessionCount = () => {
    if (["consultation", "individual"].includes(serviceDetails?.type)) {
      return `${serviceDetails?.session.count} session`
    } else if (["bundle"].includes(serviceDetails?.type)) {
      return pluralize(serviceDetails?.session.count, "session")
    } else if (["recurring"].includes(serviceDetails?.type)) {
      return (
        <div>
          {pluralize(serviceDetails?.session?.count, "session")}
          <span> / {`${serviceDetails?.billing?.unit}`}</span>
        </div>
      )
    } else {
      return "Not Found"
    }
  }

  const getStartDate = (): { startMessage: string } => {
    const meetingDates = serviceDetails?.group?.meetingDates || []
    const userTz = moment.tz.guess()

    const upcomingDates = meetingDates
      .map((date: string, index: number) => {
        const startDate = new Date(date)
        const sessionIndex = index + 1
        const formattedDate = moment
          .tz(startDate, userTz)
          .format("MMM DD [at] h:mm A z")

        return { startDate, sessionIndex, formattedDate }
      })
      .filter((session: any) => session.startDate >= new Date())
      .sort((a: any, b: any) => a.startDate - b.startDate)

    if (upcomingDates.length === 0)
      return { startMessage: "No upcoming sessions" }

    const nextSession = upcomingDates[0]
    const secondSession = upcomingDates[1]

    const isSmallScreen =
      typeof window !== "undefined" && window.innerWidth < 640

    let startMessage = ""
    if (isSmallScreen) {
      if (nextSession) {
        startMessage = `${getOrdinal(nextSession.sessionIndex)}: ${
          nextSession.formattedDate
        }`
      }
      if (secondSession) {
        startMessage = `${getOrdinal(secondSession.sessionIndex)}: ${
          secondSession.formattedDate
        }`
      }
    } else {
      if (nextSession) {
        startMessage = `${getOrdinal(
          nextSession.sessionIndex
        )} Session Scheduled For ${nextSession.formattedDate}`
      }
      if (secondSession) {
        startMessage = `${getOrdinal(
          secondSession.sessionIndex
        )} Session Scheduled For ${secondSession.formattedDate}`
      }
    }

    return { startMessage }
  }

  const getRecurrenceMessage = (): string[] => {
    const { expiration, group } = serviceDetails || {}

    if (!expiration?.expires || !group?.weeklyDayTimes?.length) return []

    const getDayName = (dayIndex: number): string =>
      moment()
        .isoWeekday(dayIndex + 1)
        .format("dddd")

    const getTimeWithZone = (time: string): string => {
      const userTz = moment.tz.guess()
      return moment.tz(time, "HH:mm", userTz).format("h:mm A z")
    }

    const getMonthlyPrefix = (freq: number): string => {
      const ordinals = ["First", "Second", "Third", "Fourth", "Fifth"]
      return ordinals[freq] || ""
    }

    return group.weeklyDayTimes.map(({ day, freq, time }: any) => {
      const dayName = getDayName(day)
      const timeWithZone = getTimeWithZone(time)

      if (["monthly", "month", "months"].includes(expiration.type || "")) {
        const prefix = getMonthlyPrefix(freq)
        return `${prefix} ${dayName}s @ ${timeWithZone}`
      } else {
        return `${dayName}s @ ${timeWithZone}`
      }
    })
  }

  const getGroupServiceEnrollment = async () => {
    console.log("running")
    try {
      const response = await ServiceRequests.checkGroupServiceEnrollment({
        stripe_product_id: service?.stripe_product_id,
      })
      const spots = response.spots_left
      setSpotsLeft(spots)
      return spots
    } catch (error) {
      console.error("Error fetching group service enrollment:", error)
      return null
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        recurrencePopoverRef.current &&
        recurrenceTriggerRef.current &&
        !recurrencePopoverRef.current.contains(event.target as Node) &&
        !recurrenceTriggerRef.current.contains(event.target as Node)
      ) {
        setShowRecurrencePopover(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (groupService) {
      getGroupServiceEnrollment()
    }
  }, [groupService, service?.stripe_product_id])

  return (
    <div
      className={`w-full md:w-[385px] bg-white relative rounded-[16px] main-shadow min-h-[410px] hover:transform hover:scale-105 transition-transform duration-300 shadow-custom last:mb-[30px] md:last:mb-0 
    ${
      groupService && spotsLeft !== null && spotsLeft <= 0
        ? ""
        : "cursor-pointer "
    }`}
      {...(!(groupService && spotsLeft !== null && spotsLeft <= 0) && {
        onClick: getServiceCardDetailsLink,
      })}
    >
      <div>
        <div
          className={`w-full h-[110px] border-white border-[12px] rounded-[20px] relative ${
            serviceDetails.image.exists ? "" : serviceDetails.image.color || ""
          }`}
          style={{
            backgroundColor: serviceDetails.image.exists
              ? undefined
              : serviceDetails.image.color
              ? undefined
              : coachColor,
            backgroundImage: serviceDetails.image.exists
              ? `url(https://clientservice-images-${
                  process.env.REACT_APP_USER_ENVIRONMENT
                }.s3.amazonaws.com/${
                  service.stripe_product_id
                }?t=${new Date().getTime()})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: "8px",
          }}
        >
          <ServiceCardBannerOverlay service={service} />
        </div>
        <div className="w-full flex flex-col px-[16px] py-[12px]">
          <div className="w-full flex flex-col items-left justify-between">
            <h4
              className={`font-bold w-full break-words ${
                serviceDetails?.title?.length > 100
                  ? "text-[14px]"
                  : "text-[18px]"
              }`}
            >
              {serviceDetails?.title}
            </h4>
            <h3 className="font-bold text-[16px] mt-2">
              {getPrice(
                parseInt(serviceDetails?.billing?.amount),
                serviceDetails?.symbol
              )}
              <span className="text-graySlate text-[12px] text-base font-normal">
                {getUnits(
                  serviceDetails.billing.unit,
                  parseInt(serviceDetails?.billing?.amount),
                  serviceDetails?.type
                )}
              </span>
              {is_group_service &&
                (() => {
                  const recurrenceMessages = getRecurrenceMessage?.() || []

                  return service?.service_details.type === "recurring" ? (
                    recurrenceMessages.length > 1 ? (
                      <div className="mt-2">
                        <button
                          ref={recurrenceTriggerRef}
                          className="text-blurple text-sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setShowRecurrencePopover((prev) => !prev)
                          }}
                        >
                          Click here to see scheduled dates
                        </button>

                        {showRecurrencePopover && (
                          <div
                            ref={recurrencePopoverRef}
                            className="absolute z-10 mt-2 p-3 bg-white border rounded shadow-md whitespace-nowrap"
                          >
                            {recurrenceMessages.map((msg, idx) => (
                              <div
                                key={idx}
                                className="mb-1 font-normal text-sm"
                              >
                                {msg}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2">
                        <Badge variant="secondary">
                          {recurrenceMessages[0]}
                        </Badge>
                      </div>
                    )
                  ) : (
                    getStartDate()?.startMessage && (
                      <div className="mt-2 -ml-2 sm:-ml-0">
                        <Badge variant="secondary">
                          {getStartDate().startMessage}
                        </Badge>
                      </div>
                    )
                  )
                })()}
            </h3>
          </div>

          <div className="w-full flex items-center justify-between mt-2">
            <div className="flex gap-[16px] items-center">
              <div
                className="py-[4px] px-[8px] rounded-[8px] flex items-center justify-center font-bold text-base text-black"
                style={{ background: textColor }}
              >
                {getSessionCount()}
              </div>
              <div
                className="py-[4px] px-[8px] rounded-[8px] flex items-center justify-center font-bold text-base text-black"
                style={{ background: textColor }}
              >
                <div>
                  {parseInt(serviceDetails?.session?.duration) / 60} minutes
                </div>
              </div>
            </div>
          </div>
          {(service.availability_status === "inactive" ||
            service.is_group_service === true) && (
            <div className="absolute bottom-[72px] w-[90%] left-[50%] -translate-x-[50%]">
              <Badge
                variant="alert"
                className="w-full flex justify-center items-center whitespace-normal break-words"
              >
                <SvgClock className="shrink-0" />
                <span className="ml-2">Scheduling managed by coach</span>
              </Badge>
            </div>
          )}
          <Button
            className={`gap-[8px] absolute bottom-[16px] w-[90%] left-[50%] -translate-x-[50%] bg-${coachColor} text-${getTextColor(
              coachColor
            )} h-[50px] rounded-md shadow-md flex items-center justify-center p-4 font-bold text-md`}
            style={{
              backgroundColor: coachColor,
              color: getTextColor(coachColor),
            }}
            onClick={getServiceCardDetailsLink}
            disabled={groupService && spotsLeft !== null && spotsLeft <= 0}
          >
            {groupService && spotsLeft !== null && spotsLeft <= 0
              ? "No Spots Availabile"
              : "See Details"}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PublicProfileServiceCard
